/**
 * Onboarding Wizard — Super-Admin tool to create a new tenant.
 * Single-screen progressive form for MVP simplicity. Marketplace visibility
 * stays OFF by default; Super-Admin must approve it separately.
 *
 * Route: /platform/onboarding
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Navigate } from "react-router-dom";
import { localizeError } from "@/lib/errors";
import {
  Sparkles, Store, MapPin, Briefcase, Lock, Plus, Trash2, CheckCircle2, RefreshCw,
} from "lucide-react";

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-4 sm:p-5">
    <div className="flex items-center gap-2 text-stone-700 font-semibold mb-3">
      <Icon size={16} className="text-[#7A1F3D]" />
      <span>{title}</span>
    </div>
    {children}
  </div>
);

const Input = ({ label, value, onChange, placeholder, dir, testId }) => (
  <div>
    <label className="block text-[11px] text-stone-500 mb-1">{label}</label>
    <input
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      dir={dir}
      className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
    />
  </div>
);

export default function OnboardingWizard() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLanguage();
  const isAdmin = user && user.role === "admin";

  const [form, setForm] = useState({
    name_en: "", name_ar: "", vertical: "beauty_salon",
    owner_name: "", owner_phone: "", owner_email: "",
    branches: [{ name: "", phone: "", address: "", city_id: "", lat: "", lng: "" }],
    services: [{ name_en: "", name_ar: "", price: "", duration_min: "30" }],
    locale_default: "ar",
    request_marketplace_visibility: false,
  });
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [created, setCreated] = useState(null);

  if (authLoading) return <div className="p-8 text-center text-stone-500">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center"><Lock className="mx-auto text-rose-500 mb-3" size={36} /><h2 className="text-xl font-bold text-stone-800">Super-Admin only</h2></div>
    </div>
  );

  const addBranch = () => setForm({ ...form, branches: [...form.branches, { name: "", phone: "", address: "", city_id: "", lat: "", lng: "" }] });
  const removeBranch = (i) => setForm({ ...form, branches: form.branches.filter((_, idx) => idx !== i) });
  const addService = () => setForm({ ...form, services: [...form.services, { name_en: "", name_ar: "", price: "", duration_min: "30" }] });
  const removeService = (i) => setForm({ ...form, services: form.services.filter((_, idx) => idx !== i) });

  const buildPayload = () => ({
    name_en: form.name_en.trim(),
    name_ar: form.name_ar.trim(),
    vertical: form.vertical,
    owner_name: form.owner_name || null,
    owner_phone: form.owner_phone || null,
    owner_email: form.owner_email || null,
    branches: form.branches.filter(b => b.name.trim()).map(b => ({
      name: b.name, phone: b.phone || null, address: b.address || null,
      city_id: b.city_id || null,
      lat: b.lat ? Number(b.lat) : null,
      lng: b.lng ? Number(b.lng) : null,
    })),
    services: form.services.filter(s => s.name_en.trim() || s.name_ar.trim()).map(s => ({
      name_en: s.name_en, name_ar: s.name_ar,
      price: Number(s.price) || 0, duration_min: Number(s.duration_min) || 30,
    })),
    locale_default: form.locale_default,
    request_marketplace_visibility: !!form.request_marketplace_visibility,
  });

  const runDryRun = async () => {
    if (!form.name_en && !form.name_ar) { toast.error("أدخل اسم الصالون"); return; }
    setBusy(true);
    try {
      const r = await api.post("/platform/onboarding/dry-run", buildPayload());
      setDryRun(r.data);
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally { setBusy(false); }
  };

  const create = async () => {
    if (!confirm("سيُنشأ tenant جديد. Marketplace visibility ستبقى OFF حتى موافقة Super-Admin. متابعة؟")) return;
    setBusy(true);
    try {
      const r = await api.post("/platform/onboarding/create", buildPayload(), {
        headers: { "X-Confirm-Action": "true" }
      });
      setCreated(r.data);
      toast.success(`Tenant جديد: ${r.data.tenant_id}`);
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally { setBusy(false); }
  };

  if (created) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="onboarding-success">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
          <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={42} />
          <h2 className="text-2xl font-bold text-stone-800">تم إنشاء Tenant بنجاح</h2>
          <div className="mt-4 inline-block text-start text-sm text-stone-700 bg-white rounded-lg p-4 border border-stone-200">
            <div><strong>Tenant ID:</strong> <code>{created.tenant_id}</code></div>
            <div><strong>Slug:</strong> <code>{created.slug}</code></div>
            <div><strong>Listing ID:</strong> <code>{created.listing_id}</code></div>
            <div><strong>Branches:</strong> {created.branches_created}</div>
            <div><strong>Services:</strong> {created.services_created}</div>
          </div>
          <p className="text-xs text-stone-600 mt-4 max-w-md mx-auto">{created.notice}</p>
          <button onClick={() => { setCreated(null); setDryRun(null); }} className="mt-5 px-4 py-2 rounded-lg bg-[#1a1015] text-white text-sm" data-testid="onboarding-restart">
            إنشاء tenant آخر
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="onboarding-page">
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500">
          <Sparkles size={14} className="text-[#7A1F3D]" /> Platform · Onboarding
        </div>
        <h1 className="text-2xl font-bold text-stone-800 mt-1">Onboarding صالون جديد</h1>
        <p className="text-sm text-stone-500 mt-1">إنشاء tenant + فروع + خدمات + listing تلقائي. ظهور Marketplace يبقى OFF حتى موافقة Super-Admin.</p>
      </div>

      <Section title="بيانات الصالون" icon={Store}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testId="onb-name-ar" label="الاسم (عربي)" value={form.name_ar} onChange={(v) => setForm({ ...form, name_ar: v })} placeholder="صالون النخبة" />
          <Input testId="onb-name-en" label="Name (English)" value={form.name_en} onChange={(v) => setForm({ ...form, name_en: v })} placeholder="Elite Salon" dir="ltr" />
          <div>
            <label className="block text-[11px] text-stone-500 mb-1">القطاع</label>
            <select data-testid="onb-vertical" value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm">
              <option value="beauty_salon">{t("business_type_beauty_salon")}</option>
              <option value="hair_salon">{lang === "ar" ? "صالون شعر" : "Hair Salon"}</option>
              <option value="spa">{lang === "ar" ? "سبا" : "Spa"}</option>
              <option value="nail_salon">{t("business_type_nail_salon")}</option>
              <option value="massage_center">{t("business_type_massage_center")}</option>
              <option value="wellness_center">{t("business_type_wellness_center")}</option>
              <option value="barber">{lang === "ar" ? "حلاق" : "Barber"}</option>
              <option value="gym_fitness">{lang === "ar" ? "جيم / لياقة" : "Gym / Fitness"}</option>
            </select>
          </div>
          <Input testId="onb-owner-name" label="اسم المالك" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} />
          <Input testId="onb-owner-phone" label="هاتف المالك" value={form.owner_phone} onChange={(v) => setForm({ ...form, owner_phone: v })} dir="ltr" placeholder="+97455123456" />
          <Input testId="onb-owner-email" label="بريد المالك" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} dir="ltr" />
        </div>
      </Section>

      <Section title="الفروع" icon={MapPin}>
        <div className="space-y-3">
          {form.branches.map((b, i) => (
            <div key={i} className="p-3 rounded-lg bg-stone-50 grid sm:grid-cols-6 gap-2 items-end" data-testid={`onb-branch-${i}`}>
              <div className="sm:col-span-2"><Input label={`الفرع ${i + 1}`} value={b.name} onChange={(v) => { const n = [...form.branches]; n[i].name = v; setForm({ ...form, branches: n }); }} placeholder="الفرع الرئيسي" /></div>
              <Input label="هاتف" value={b.phone} onChange={(v) => { const n = [...form.branches]; n[i].phone = v; setForm({ ...form, branches: n }); }} dir="ltr" />
              <Input label="مدينة" value={b.city_id} onChange={(v) => { const n = [...form.branches]; n[i].city_id = v; setForm({ ...form, branches: n }); }} placeholder="doha" dir="ltr" />
              <Input label="Lat" value={b.lat} onChange={(v) => { const n = [...form.branches]; n[i].lat = v; setForm({ ...form, branches: n }); }} placeholder="25.286" dir="ltr" />
              <div className="flex gap-2">
                <Input label="Lng" value={b.lng} onChange={(v) => { const n = [...form.branches]; n[i].lng = v; setForm({ ...form, branches: n }); }} placeholder="51.531" dir="ltr" />
                <button type="button" onClick={() => removeBranch(i)} className="self-end px-2 py-2 text-rose-600 hover:bg-rose-50 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <button type="button" data-testid="onb-add-branch" onClick={addBranch} className="inline-flex items-center gap-1.5 text-sm text-[#7A1F3D] hover:underline">
            <Plus size={14} /> إضافة فرع
          </button>
        </div>
      </Section>

      <Section title="الخدمات الأساسية" icon={Briefcase}>
        <div className="space-y-3">
          {form.services.map((s, i) => (
            <div key={i} className="p-3 rounded-lg bg-stone-50 grid sm:grid-cols-5 gap-2 items-end" data-testid={`onb-service-${i}`}>
              <Input label={`الاسم AR`} value={s.name_ar} onChange={(v) => { const n = [...form.services]; n[i].name_ar = v; setForm({ ...form, services: n }); }} placeholder="قص شعر" />
              <Input label="Name EN" value={s.name_en} onChange={(v) => { const n = [...form.services]; n[i].name_en = v; setForm({ ...form, services: n }); }} placeholder="Haircut" dir="ltr" />
              <Input label="السعر (QAR)" value={s.price} onChange={(v) => { const n = [...form.services]; n[i].price = v; setForm({ ...form, services: n }); }} placeholder="80" dir="ltr" />
              <Input label="المدة (دقيقة)" value={s.duration_min} onChange={(v) => { const n = [...form.services]; n[i].duration_min = v; setForm({ ...form, services: n }); }} placeholder="30" dir="ltr" />
              <button type="button" onClick={() => removeService(i)} className="self-end px-2 py-2 text-rose-600 hover:bg-rose-50 rounded justify-self-start"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" data-testid="onb-add-service" onClick={addService} className="inline-flex items-center gap-1.5 text-sm text-[#7A1F3D] hover:underline">
            <Plus size={14} /> إضافة خدمة
          </button>
        </div>
      </Section>

      <Section title="Marketplace" icon={Sparkles}>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="onb-marketplace-toggle">
          <input type="checkbox" checked={form.request_marketplace_visibility} onChange={(e) => setForm({ ...form, request_marketplace_visibility: e.target.checked })} className="mt-1" />
          <div>
            <div className="text-sm font-medium text-stone-800">اطلب ظهور Marketplace</div>
            <div className="text-[11px] text-stone-500 mt-0.5">سيُنشأ listing بـ visibility=false دائماً. يفعّل Super-Admin الظهور لاحقاً يدوياً.</div>
          </div>
        </label>
      </Section>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <button data-testid="onb-dry-run" disabled={busy} onClick={runDryRun} className="px-4 py-2 rounded-lg border border-stone-200 hover:border-[#7A1F3D] text-sm bg-white">
          {busy ? "..." : "Dry run (تحقّق)"}
        </button>
        <button data-testid="onb-create" disabled={busy || !dryRun?.slug_available} onClick={create} className={`px-5 py-2.5 rounded-lg text-white text-sm inline-flex items-center gap-2 ${dryRun?.slug_available ? "bg-gradient-to-r from-[#7A1F3D] to-[#9B2950]" : "bg-stone-300"}`}>
          {busy && <RefreshCw size={14} className="animate-spin" />} إنشاء Tenant
        </button>
      </div>

      {dryRun && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-900" data-testid="onb-dry-run-result">
          <div><strong>Slug:</strong> <code>{dryRun.slug}</code> {dryRun.slug_available ? <span className="text-emerald-700 text-xs">✓ متاح</span> : <span className="text-rose-700 text-xs">✗ مستخدم</span>}</div>
          <div><strong>الفروع:</strong> {dryRun.branches} · <strong>الخدمات:</strong> {dryRun.services} · <strong>القطاع:</strong> {dryRun.vertical}</div>
        </div>
      )}
    </div>
  );
}
